import { useEffect, useState, useCallback } from 'react';
import { foroAdminApi, type ForoTemaAdmin, type ForoPostAdmin } from '../lib/api';
import { Toast, type ToastState } from '../components/Toast';

const CATS = [
  { id: 'ANSIEDAD',             label: 'Ansiedad',    tone: 'copper' },
  { id: 'DEPRESIÓN',            label: 'Depresión',   tone: 'info'   },
  { id: 'AUTOESTIMA',           label: 'Autoestima',  tone: 'sage'   },
  { id: 'RELACIONES',           label: 'Relaciones',  tone: 'clay'   },
  { id: 'ESTRÉS',               label: 'Estrés',      tone: 'amber'  },
  { id: 'DUELO',                label: 'Duelo',       tone: 'sand'   },
  { id: 'TRAUMA',               label: 'Trauma',      tone: 'info'   },
  { id: 'MINDFULNESS',          label: 'Mindfulness', tone: 'sage'   },
  { id: 'LÍMITES',              label: 'Límites',     tone: 'copper' },
  { id: 'CRECIMIENTO PERSONAL', label: 'Crecimiento', tone: 'amber'  },
];

const TONE: Record<string, { soft: string; ink: string; solid: string; line: string }> = {
  copper: { soft: 'var(--copper-soft)', ink: 'var(--copper-ink)', solid: 'var(--copper)', line: 'var(--copper-line)' },
  sage:   { soft: 'var(--sage-soft)',   ink: 'var(--sage-ink)',   solid: 'var(--sage)',   line: 'var(--sage-line)'   },
  amber:  { soft: 'var(--amber-soft)',  ink: 'var(--amber)',      solid: 'var(--amber)',  line: '#e7d7ab'            },
  clay:   { soft: 'var(--clay-soft)',   ink: 'var(--clay)',       solid: 'var(--clay)',   line: '#e7c9c1'            },
  info:   { soft: 'var(--info-soft)',   ink: 'var(--info)',       solid: 'var(--info)',   line: '#cbd9e2'            },
  sand:   { soft: 'var(--sand-soft)',   ink: '#7a6047',           solid: 'var(--sand)',   line: '#e3d3bd'            },
};

const catById = Object.fromEntries(CATS.map((c) => [c.id, c]));

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()} ${['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'][d.getMonth()]} ${d.getFullYear()}`;
}

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60)  return `hace ${m || 1}m`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `hace ${h}h`;
  const d = Math.floor(h / 24);
  return `hace ${d}d`;
}

function CatTag({ cat, small }: { cat: string; small?: boolean }) {
  const c = catById[cat];
  if (!c) return <span className="tag">{cat}</span>;
  const t = TONE[c.tone];
  return (
    <span className="tag" style={{ background: t.soft, color: t.ink, borderColor: t.line, height: small ? 21 : 23 }}>
      <span className="dot" style={{ background: t.solid }} />
      {c.label}
    </span>
  );
}

/* ── Create tema modal ── */
function CreateTemaModal({ onClose, onCreate }: {
  onClose: () => void;
  onCreate: (data: { titulo: string; descripcion?: string; categoria: string }) => void;
}) {
  const [titulo,   setTitulo]   = useState('');
  const [desc,     setDesc]     = useState('');
  const [cat,      setCat]      = useState('');
  const [saving,   setSaving]   = useState(false);

  const ready = titulo.trim() && cat;

  const submit = () => {
    if (!ready) return;
    setSaving(true);
    onCreate({ titulo: titulo.trim(), descripcion: desc.trim() || undefined, categoria: cat });
  };

  return (
    <div className="modal" onClick={() => !saving && onClose()}
      style={{ background: 'rgba(47,37,25,.38)', backdropFilter: 'blur(2px)' }}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '22px 24px 0' }}>
          <div className="row" style={{ gap: 11 }}>
            <span className="stat-ico" style={{ background: 'var(--info-soft)', color: 'var(--info)', width: 38, height: 38 }}>
              <ChatIco size={19} />
            </span>
            <div>
              <h3 style={{ margin: 0, fontSize: 18 }}>Nuevo tema</h3>
              <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>Crea un tema de discusión</p>
            </div>
            <button className="btn btn-ghost btn-icon" style={{ marginLeft: 'auto' }} onClick={onClose} disabled={saving}>
              <XIco size={17} />
            </button>
          </div>
        </div>
        <hr className="hr" style={{ margin: '18px 0 0' }} />

        {!saving ? (
          <div style={{ padding: 24 }}>
            <label className="field-label">Título</label>
            <input className="input" style={{ marginBottom: 14 }}
              placeholder="Ej. ¿Cómo manejar la ansiedad social?"
              value={titulo} onChange={(e) => setTitulo(e.target.value)} />

            <label className="field-label">Descripción (opcional)</label>
            <textarea className="input" rows={3} style={{ marginBottom: 14, resize: 'none' }}
              placeholder="Contexto o motivación del tema"
              value={desc} onChange={(e) => setDesc(e.target.value)} />

            <label className="field-label">Categoría</label>
            <div className="row" style={{ flexWrap: 'wrap', gap: 7 }}>
              {CATS.map((c) => (
                <button key={c.id} onClick={() => setCat(c.id === cat ? '' : c.id)}
                  style={{ cursor: 'pointer', height: 28, padding: '0 11px', fontSize: 12, fontWeight: 600,
                    border: cat === c.id ? `1px solid ${TONE[c.tone].solid}` : '1px solid var(--border)',
                    borderRadius: 99, background: cat === c.id ? TONE[c.tone].soft : 'var(--surface-2)',
                    color: cat === c.id ? TONE[c.tone].ink : 'var(--ink-2)' }}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ padding: '40px 24px', textAlign: 'center' }}>
            <div style={{ color: 'var(--info)', marginBottom: 14 }}><RefreshIco size={28} /></div>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 18 }}>Creando tema…</div>
          </div>
        )}

        {!saving && (
          <div className="drawer-foot" style={{ justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={submit} disabled={!ready}>
              <ChatIco size={16} /> Crear tema
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Posts panel (expandable inline) ── */
function PostsPanel({ tema, onDelete }: { tema: ForoTemaAdmin; onDelete: (id: string) => void }) {
  const [posts,   setPosts]   = useState<ForoPostAdmin[]>([]);
  const [loading, setLoad]    = useState(true);

  useEffect(() => {
    setLoad(true);
    foroAdminApi.getPosts(tema.id)
      .then((r) => {
        // r is { posts, total } — flatten replies for admin view
        const flat: ForoPostAdmin[] = [];
        const nested = (r as unknown as { posts: (ForoPostAdmin & { replies?: ForoPostAdmin[] })[] }).posts;
        nested.forEach((p) => {
          flat.push(p);
          (p.replies ?? []).forEach((r) => flat.push(r));
        });
        setPosts(flat);
      })
      .catch(() => setPosts([]))
      .finally(() => setLoad(false));
  }, [tema.id]);

  if (loading) return (
    <div style={{ padding: '12px 16px' }}>
      <span className="spinner spinner--sm" /> Cargando posts…
    </div>
  );

  if (posts.length === 0) return (
    <div style={{ padding: '16px', fontSize: 13, color: 'var(--muted)' }}>Sin posts aún.</div>
  );

  return (
    <div style={{ borderTop: '1px solid var(--border)' }}>
      {posts.map((p) => (
        <div key={p.id} className="row" style={{ padding: '12px 18px', gap: 12,
          borderBottom: '1px solid var(--border)', alignItems: 'flex-start',
          marginLeft: p.parent_id ? 28 : 0,
          background: p.parent_id ? 'var(--surface-2)' : 'transparent' }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%',
            background: 'var(--info-soft)', color: 'var(--info)',
            display: 'grid', placeItems: 'center', flexShrink: 0,
            fontWeight: 700, fontSize: 13 }}>
            {p.nombre_usuario[0]?.toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="row" style={{ gap: 8, marginBottom: 4 }}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>{p.nombre_usuario}</span>
              {p.parent_id && <span className="tag" style={{ fontSize: 10.5, height: 18 }}>↳ respuesta</span>}
              <span className="muted" style={{ fontSize: 11.5, marginLeft: 'auto' }}>{relTime(p.created_at)}</span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
              {p.contenido}
            </div>
          </div>
          <button className="btn btn-sm btn-ghost" style={{ color: 'var(--clay)', flexShrink: 0 }}
            onClick={() => { if (window.confirm('¿Eliminar este post?')) onDelete(p.id); }}>
            <TrashIco size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

/* ── Main page ── */
export default function Comunidad() {
  const [temas,    setTemas]   = useState<ForoTemaAdmin[]>([]);
  const [loading,  setLoad]    = useState(true);
  const [toast,    setToast]   = useState<ToastState | null>(null);
  const [createOpen, setCreate] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const showToast = useCallback((msg: string, type: ToastState['type']) =>
    setToast({ message: msg, type }), []);

  const loadTemas = useCallback(() => {
    setLoad(true);
    foroAdminApi.getTemas()
      .then((r) => setTemas(r.temas))
      .catch(() => setTemas([]))
      .finally(() => setLoad(false));
  }, []);

  useEffect(() => { loadTemas(); }, [loadTemas]);

  const handleCreate = async (data: { titulo: string; descripcion?: string; categoria: string }) => {
    try {
      await foroAdminApi.createTema(data);
      showToast('Tema creado', 'success');
      setCreate(false);
      loadTemas();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Error al crear', 'error');
      setCreate(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    if (!window.confirm('¿Desactivar este tema? Dejará de aparecer en la comunidad.')) return;
    try {
      await foroAdminApi.deleteTema(id);
      showToast('Tema desactivado', 'success');
      setTemas((prev) => prev.filter((t) => t.id !== id));
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Error', 'error');
    }
  };

  const handleDeletePost = async (id: string) => {
    try {
      await foroAdminApi.deletePost(id);
      showToast('Post eliminado', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Error', 'error');
    }
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div className="page page-wide">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: 28, margin: 0 }}>Comunidad</h1>
          <p className="muted" style={{ margin: '7px 0 0', fontSize: 14, maxWidth: 560 }}>
            Gestiona los temas del foro y modera las publicaciones de los usuarios.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setCreate(true)}>
          <ChatIco size={16} /> Crear tema
        </button>
      </div>

      {/* Stats */}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 28 }}>
        <div className="card stat fade-up">
          <div className="stat-top">
            <span className="stat-ico" style={{ background: 'var(--info-soft)', color: 'var(--info)' }}><ChatIco /></span>
          </div>
          <div className="stat-num tabular">{temas.length}</div>
          <div className="stat-label">Temas activos</div>
        </div>
        <div className="card stat fade-up">
          <div className="stat-top">
            <span className="stat-ico" style={{ background: 'var(--sage-soft)', color: 'var(--sage)' }}><UsersIco /></span>
          </div>
          <div className="stat-num tabular">
            {temas.reduce((s, t) => s + (t.post_count ?? 0), 0)}
          </div>
          <div className="stat-label">Posts totales</div>
        </div>
        <div className="card stat fade-up">
          <div className="stat-top">
            <span className="stat-ico" style={{ background: 'var(--copper-soft)', color: 'var(--copper)' }}><LayersIco /></span>
          </div>
          <div className="stat-num tabular">
            {new Set(temas.map((t) => t.categoria)).size}
            <span style={{ fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 400, color: 'var(--muted)' }}>
              /{CATS.length}
            </span>
          </div>
          <div className="stat-label">Categorías con temas</div>
        </div>
      </div>

      {loading && <div className="loading-center"><span className="spinner" /> Cargando temas…</div>}

      {!loading && temas.length === 0 && (
        <div style={{ padding: '60px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 17 }}>Sin temas</div>
          <div style={{ fontSize: 13, marginTop: 4, color: 'var(--muted)' }}>
            Crea el primer tema para activar la comunidad.
          </div>
        </div>
      )}

      {!loading && temas.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {temas.map((tema) => {
            const isOpen = expanded.has(tema.id);
            return (
              <div key={tema.id} className="card fade-up" style={{ overflow: 'hidden' }}>
                {/* Tema header */}
                <div className="card-pad row" style={{ gap: 14, alignItems: 'flex-start',
                  paddingBottom: isOpen ? 12 : 16 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="row" style={{ gap: 8, marginBottom: 6 }}>
                      <CatTag cat={tema.categoria} small />
                      <span className="muted" style={{ fontSize: 11.5 }}>{fmtDate(tema.created_at)}</span>
                    </div>
                    <div style={{ fontFamily: 'var(--serif)', fontSize: 16.5, marginBottom: 4 }}>
                      {tema.titulo}
                    </div>
                    {tema.descripcion && (
                      <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.55,
                        overflow: 'hidden', display: '-webkit-box',
                        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {tema.descripcion}
                      </div>
                    )}
                    <div className="row" style={{ gap: 12, marginTop: 8, fontSize: 12,
                      color: 'var(--ink-2)' }}>
                      <span><ChatIco size={12} /> {tema.post_count ?? 0} posts</span>
                      {tema.ultima_actividad && (
                        <span>🕐 {relTime(tema.ultima_actividad)}</span>
                      )}
                    </div>
                  </div>

                  <div className="row" style={{ gap: 6, flexShrink: 0, marginTop: 2 }}>
                    <button className="btn btn-sm btn-ghost" onClick={() => toggleExpand(tema.id)}>
                      {isOpen ? <ChevronUpIco size={14} /> : <ChevronDownIco size={14} />}
                      {isOpen ? 'Ocultar' : 'Ver posts'}
                    </button>
                    <button className="btn btn-sm btn-ghost" style={{ color: 'var(--clay)' }}
                      onClick={() => handleDeactivate(tema.id)}>
                      Desactivar
                    </button>
                  </div>
                </div>

                {/* Posts panel */}
                {isOpen && (
                  <PostsPanel tema={tema}
                    onDelete={(postId) => handleDeletePost(postId)} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {createOpen && <CreateTemaModal onClose={() => setCreate(false)} onCreate={handleCreate} />}

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}

/* ── Icons ── */
function Svg({ children, size = 18 }: { children: React.ReactNode; size?: number }) {
  return <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{children}</svg>;
}
function ChatIco({ size = 18 }: { size?: number })      { return <Svg size={size}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></Svg>; }
function XIco({ size = 18 }: { size?: number })         { return <Svg size={size}><path d="M6 6l12 12M18 6 6 18"/></Svg>; }
function RefreshIco({ size = 18 }: { size?: number })   { return <Svg size={size}><path d="M3 12a9 9 0 0 1 15.5-6.3M21 4v4h-4"/><path d="M21 12a9 9 0 0 1-15.5 6.3M3 20v-4h4"/></Svg>; }
function TrashIco({ size = 18 }: { size?: number })     { return <Svg size={size}><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"/></Svg>; }
function UsersIco({ size = 18 }: { size?: number })     { return <Svg size={size}><circle cx="9" cy="8" r="3.2"/><path d="M3.5 20a5.5 5.5 0 0 1 11 0"/><path d="M16 5.2a3.2 3.2 0 0 1 0 6"/><path d="M17 14.4A5.5 5.5 0 0 1 20.5 20"/></Svg>; }
function LayersIco({ size = 18 }: { size?: number })    { return <Svg size={size}><path d="M12 3l8 4.5-8 4.5-8-4.5z"/><path d="M4 12l8 4.5L20 12"/><path d="M4 16.5L12 21l8-4.5"/></Svg>; }
function ChevronDownIco({ size = 18 }: { size?: number }) { return <Svg size={size}><path d="M6 9l6 6 6-6"/></Svg>; }
function ChevronUpIco({ size = 18 }: { size?: number })   { return <Svg size={size}><path d="M18 15l-6-6-6 6"/></Svg>; }
